import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
} from "@mui/material";
import axios from "axios";

import {
  Add,
  Search,
  Settings,
  Apartment,
  CheckCircle,
  Assignment,
  Lock,
  InfoOutlined,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { Skeleton } from "@mui/material";

import Wrapper from "../../../components/Wrapper";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PropertySettingModal, {
  isAvailableForNewSetting,
  isRented,
} from "../../../components/PropertySettingModal";

import { useDispatch } from "react-redux";
import { GetProperty } from "../../../redux/reducer/GetPropertyDetails";
import { BASE_URL } from "../../../config/config";
import { toast, ToastContainer } from "react-toastify";

const PropertySetting = () => {
  const open = useSelector((state) => state.sidebar.open);

  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  /* Flat id carried over from "Property added → apply settings", so the owner
     lands with the right flat already ticked instead of hunting for it. */
  const [preselected, setPreselected] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const { validateUser } = useSelector((state) => state);

  const [properties, setProperties] = useState([]);

  const [loading, setLoading] = useState(false);
  const [propertiesLoaded, setPropertiesLoaded] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const [settings, setSettings] = useState([]);
  useEffect(() => {
    if (validateUser?.UserValidate?.data?.user?._id) {
      fetchSettings();
    }
  }, [validateUser?.UserValidate?.data?.user?._id]);
  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);

      const userId = validateUser?.UserValidate?.data?.user?._id;

      const res = await axios.get(`${BASE_URL}/property-settings/${userId}`);

      if (res.data.success) {
        // API can answer success with a null payload when nothing is saved yet
        setSettings(res.data.data || []);
      } else {
        setSettings([]);
      }
    } catch (error) {
      console.log(error);
      setSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  };
  //   const settings = [
  //     {
  //       id: 1,
  //       name: "Family Property Rules",
  //       description: "Rules for family tenants",
  //       properties: 12,
  //       status: "Active",
  //     },
  //     {
  //       id: 2,
  //       name: "Bachelor Rules",
  //       description: "Rules for bachelor tenants",
  //       properties: 5,
  //       status: "Active",
  //     },
  //     {
  //       id: 3,
  //       name: "Commercial Property",
  //       description: "Office and shop settings",
  //       properties: 3,
  //       status: "Draft",
  //     },
  //   ];
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);

      const res = await dispatch(
        GetProperty({
          userId: validateUser?.UserValidate?.data?.user?._id,

          from: "landlord",
        })
      );

      if (res?.payload?.data?.success && res?.payload?.data?.data) {
        setProperties(res.payload.data.data);
      } else {
        setProperties([]);
      }
    } catch (error) {
      console.log("Property fetch error", error);

      setProperties([]);
    } finally {
      setLoading(false);
      setPropertiesLoaded(true);
    }
  }, [dispatch, validateUser?.UserValidate?.data?.user?._id]);

  useEffect(() => {
    if (validateUser?.UserValidate?.data?.user?._id) {
      fetchProperties();
    }
  }, [fetchProperties, validateUser?.UserValidate?.data?.user?._id]);

  // Opening the modal fresh vs. opening a saved setting are two different
  // modes, and `selectedSetting` is what tells them apart — so it has to be
  // cleared on every path back out, otherwise "Add New Setting" reopens the
  // last-viewed setting in read-only mode with no Save button.
  const openCreateModal = (flatIds = []) => {
    setSelectedSetting(null);
    setPreselected(flatIds);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setSelectedSetting(null);
    setPreselected([]);
  };

  /* Arriving from "Property added → apply settings" (…/properties-setting?flat=id)
     opens the form straight away with that flat ticked. The param is dropped
     afterwards so a refresh doesn't reopen the dialog. */
  useEffect(() => {
    const flatId = searchParams.get("flat");
    /* `loading` only turns true once the fetch starts, so waiting on it alone
       would fire this on the very first render with an empty list and open the
       form with nothing ticked. */
    if (!flatId || !propertiesLoaded) return;

    const flat = properties.find((p) => p._id === flatId);
    openCreateModal(flat && isAvailableForNewSetting(flat) ? [flatId] : []);

    const next = new URLSearchParams(searchParams);
    next.delete("flat");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertiesLoaded]);

  const totalSettings = settings.length;

  const totalProperties = properties.length;

  // The create buttons are gated on the SAME rule the modal uses to build its
  // property list — counting every property would leave the button enabled when
  // all of them are rented / rejected / already covered, opening a modal with
  // nothing to pick. While the fetch is still running `properties` is
  // legitimately empty, so the wording/tooltip waits for it to settle.
  const assignableCount = properties.filter(isAvailableForNewSetting).length;
  const createDisabled = loading || assignableCount === 0;

  const noProperties = !loading && totalProperties === 0;
  const nothingAssignable = !loading && assignableCount === 0;

  const createDisabledReason = noProperties
    ? "Add a property first — a setting has to be applied to at least one property."
    : nothingAssignable
    ? "No property is available: all of them are either rented out, rejected, or already covered by a setting."
    : "";

  const appliedProperties = new Set(
    settings.flatMap((s) => s.properties || [])
  ).size;

  /* What the owner should do next, in the order it actually happens:
     apply rules → admin approves → the flat goes live → it gets rented (and
     locks). Showing the counts spares them from decoding the property cards. */
  const needsSettings = properties.filter(isAvailableForNewSetting);
  const awaitingApproval = properties.filter(
    (p) => p.is_property_settings && !p.is_approved && !p.is_rejected
  );
  const rentedProperties = properties.filter(isRented);

  const settingsWithProperties = settings.map((setting) => {
    return {
      ...setting,
      propertyDetails: properties.filter((property) =>
        setting.properties.includes(property._id)
      ),
    };
  });

  const handleDeleteSetting = async (id) => {
    try {
      setLoading(true);

      const res = await axios.delete(`${BASE_URL}/property-settings/${id}`);

      if (res.data.success) {
        toast.success(res.data.message);

        // Delete also resets is_property_settings/is_approved on the freed
        // properties, so the property list is stale too.
        await Promise.all([fetchSettings(), fetchProperties()]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete setting");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Wrapper open={open} />

      <Box
        className={`${open ? "sidebar-open" : "sidebar-closed"}`}
        sx={{
          minHeight: "100vh",
          bgcolor: "transparent",
          pb: 4,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 4,
          }}
        >
          {/* Header */}

          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: 3,
              background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              boxShadow: "0 8px 32px rgba(102,126,234,0.3)",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Settings sx={{ fontSize: 36 }} />
                Property Settings
              </Typography>

              <Typography sx={{ opacity: 0.9, mt: 0.5, maxWidth: 620 }}>
                Termination &amp; violation rules, applied to one or more
                properties.
              </Typography>

              {/* The single most misunderstood rule of the whole flow, stated
                  where the owner is standing when it matters. */}
              <Box
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  maxWidth: 620,
                  bgcolor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  display: "flex",
                  gap: 1.25,
                  alignItems: "flex-start",
                }}
              >
                <InfoOutlined sx={{ fontSize: 20, mt: "2px", flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                  <strong>Without a setting a property stays invisible.</strong>{" "}
                  No tenant can see it and the admin never receives it for
                  approval. Apply a setting and approval starts within 24 hours.
                </Typography>
              </Box>
            </Box>

            {/* span wrapper: MUI swallows pointer events on a disabled button,
                so the tooltip needs a live element to hang off */}
            <Tooltip title={createDisabledReason}>
              <span>
                <Button
                  variant="contained"
                  startIcon={loading ? null : <Add />}
                  disabled={createDisabled}
                  onClick={() => openCreateModal()}
                  sx={{
                    bgcolor: "#fff",
                    color: "var(--owner-primary)",
                    px: 3,
                    py: 1.2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,

                    "&:hover": {
                      bgcolor: "#f1f5f9",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,0.45)",
                      color: "rgba(102,126,234,0.6)",
                    },
                  }}
                >
                  Add New Setting
                </Button>
              </span>
            </Tooltip>
          </Paper>

          {/* Stats */}

          <Grid container spacing={2} mb={3}>
            {[
              {
                title: "Total Settings",
                value: totalSettings,
                icon: <Assignment />,
                color: "#6366f1",
                bg: "#eef2ff",
              },

              {
                title: "Applied Properties",
                value: appliedProperties,
                icon: <CheckCircle />,
                color: "#22c55e",
                bg: "#dcfce7",
              },

              {
                title: "Total Properties",
                value: totalProperties,
                icon: <Apartment />,
                color: "#3b82f6",
                bg: "#dbeafe",
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid #e5e7eb",
                    boxShadow: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      bgcolor: item.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: item.color,
                      mb: 2,
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Typography variant="h4" fontWeight={700}>
                    {item.value}
                  </Typography>

                  <Typography color="text.secondary">{item.title}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Next step — one line, and the button that does it */}
          {!loading && needsSettings.length > 0 && (
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "color-mix(in srgb, var(--owner-primary) 35%, transparent)",
                bgcolor: "color-mix(in srgb, var(--owner-primary) 6%, #fff)",
                boxShadow: "none",
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography fontWeight={700}>
                  {needsSettings.length === 1
                    ? "1 property is waiting for its rules"
                    : `${needsSettings.length} properties are waiting for their rules`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A property is only sent to the admin for approval once a
                  setting is applied to it.
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                  {needsSettings.slice(0, 4).map((p) => (
                    <Chip
                      key={p._id}
                      size="small"
                      variant="outlined"
                      label={`${p.flatName}${
                        p.flatNumber ? ` (${p.flatNumber})` : ""
                      }`}
                    />
                  ))}
                  {needsSettings.length > 4 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`+${needsSettings.length - 4} more`}
                    />
                  )}
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openCreateModal()}
                sx={{ px: 2.5, py: 1, fontWeight: 700, flexShrink: 0 }}
              >
                Apply settings
              </Button>
            </Paper>
          )}

          {/* Nothing to do — but the owner still wants to know where things are */}
          {!loading && needsSettings.length === 0 && awaitingApproval.length > 0 && (
            <Paper
              sx={{
                p: { xs: 2, md: 2.5 },
                mb: 3,
                borderRadius: 3,
                border: "1px solid #fcd34d",
                bgcolor: "#fffbeb",
                boxShadow: "none",
              }}
            >
              <Typography fontWeight={700}>
                {awaitingApproval.length === 1
                  ? "1 property is with the admin for approval"
                  : `${awaitingApproval.length} properties are with the admin for approval`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rules are applied and everything is submitted. You'll get a
                notification as soon as it is approved and visible to tenants.
              </Typography>
            </Paper>
          )}

          {!loading && rentedProperties.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Lock sx={{ fontSize: 16 }} />
              {rentedProperties.length === 1
                ? "1 rented property is locked — its rules can be changed once the flat is vacant."
                : `${rentedProperties.length} rented properties are locked — their rules can be changed once those flats are vacant.`}
            </Typography>
          )}

          {/* Search */}

          {settings.length > 0 && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                boxShadow: "none",
              }}
            >
              <TextField
                fullWidth
                placeholder="Search settings..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                  },
                }}
              />
            </Paper>
          )}

          {/* Cards */}

          {/* Cards / No Data */}

          {settingsLoading ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Paper
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid #e5e7eb",
                      boxShadow: "none",
                    }}
                  >
                    <Skeleton variant="text" width="60%" height={35} />
                    <Skeleton variant="text" width="90%" />

                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={28}
                      sx={{ mt: 2 }}
                    />

                    <Skeleton variant="text" sx={{ mt: 2 }} />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="70%" />

                    <Skeleton
                      variant="rounded"
                      width={90}
                      height={36}
                      sx={{ mt: 3 }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : settingsWithProperties.length === 0 ? (
            <Paper
              sx={{
                py: 8,
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                boxShadow: "none",
                textAlign: "center",
                bgcolor: "#fff",
              }}
            >
              <Box>
                <Settings
                  sx={{
                    fontSize: 90,
                    color: "#cbd5e1",
                    mb: 2,
                  }}
                />

                <Typography variant="h5" fontWeight={700} color="#1e293b">
                  {noProperties
                    ? "No Properties Added Yet"
                    : nothingAssignable
                    ? "No Property Available for a Setting"
                    : "No Property Settings Found"}
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 1,
                    mb: 3,
                    maxWidth: 500,
                    mx: "auto",
                  }}
                >
                  {noProperties
                    ? "A setting has to be applied to at least one property. Add a property first, then come back to set up your termination & violation rules."
                    : nothingAssignable
                    ? "Every property you own is currently rented out, rejected, or already covered by a setting. Add a new property to create another setting."
                    : "You haven't created any property settings yet. Create your first setting and apply it to multiple properties."}
                </Typography>

                <Tooltip title={createDisabledReason}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      disabled={createDisabled}
                      onClick={() => openCreateModal()}
                      sx={{
                        background: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
                        px: 3,
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          background: "linear-gradient(135deg, #5a6fd6 0%, #6a3690 100%)",
                        },
                      }}
                    >
                      Create First Setting
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={2.5}>
              {settingsWithProperties
                .filter((item) =>
                  item.settingName?.toLowerCase().includes(search.toLowerCase())
                )
                .map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item._id}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        border: "1px solid #e5e7eb",
                        boxShadow: "none",
                        cursor: "pointer",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 12px 28px rgba(102,126,234,0.18)",
                          borderColor: "var(--owner-primary)",
                        },
                      }}
                      onClick={() => {
                        setSelectedSetting(item);
                        setOpenModal(true);
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--owner-primary)",
                            bgcolor:
                              "color-mix(in srgb, var(--owner-primary) 10%, transparent)",
                          }}
                        >
                          <Assignment fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="h6" fontWeight={700} noWrap>
                            {item.settingName}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {item.description || "No description"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Which flats this setting governs, and their state */}
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: "1px dashed #e5e7eb",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: 0.6,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Applied to {item.propertyDetails?.length || 0}{" "}
                          {item.propertyDetails?.length === 1
                            ? "property"
                            : "properties"}
                        </Typography>

                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            gap: 0.75,
                            flexWrap: "wrap",
                          }}
                        >
                          {(item.propertyDetails || []).slice(0, 4).map((property) => (
                            <Chip
                              key={property._id}
                              size="small"
                              variant="outlined"
                              icon={
                                isRented(property) ? (
                                  <Lock sx={{ fontSize: 14 }} />
                                ) : undefined
                              }
                              label={property.flatName}
                              title={
                                isRented(property)
                                  ? "Rented — locked until the flat is vacant"
                                  : property.flatNumber
                              }
                            />
                          ))}
                          {(item.propertyDetails?.length || 0) > 4 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`+${item.propertyDetails.length - 4} more`}
                            />
                          )}
                          {(item.propertyDetails?.length || 0) === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No property is using this setting.
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          mt: 2.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "var(--owner-primary)", fontWeight: 700 }}
                        >
                          Click to view rules
                        </Typography>
                        <Tooltip title="Delete setting">
                          <Button
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent the card's modal from opening
                              handleDeleteSetting(item._id);
                            }}
                            sx={{ minWidth: 0, px: 1.25 }}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}
        </Box>
      </Box>
      {openModal && (
        <PropertySettingModal
          open={openModal}
          handleClose={closeModal}
          properties={properties}
          userId={validateUser?.UserValidate?.data?.user?._id}
          existingSettings={settings}
          initialProperties={preselected}
          selectedSetting={selectedSetting}
          setSelectedSetting={setSelectedSetting}
          viewOnly={!!selectedSetting}
          saving={loading}
          handleSave={async (data) => {
            try {
              setLoading(true);

              const res = await axios.post(
                `${BASE_URL}/property-settings`,
                data
              );

              if (res.data.success) {
                // We just show the server's message — it states that as soon
                // as the setting was applied, the property went to the admin
                // for review, the same thing the owner gets by email.
                toast.success(
                  res.data.message ||
                    "Setting applied — your property has been sent to the admin for approval"
                );

                // Settings list AND properties both change here: the server
                // flips is_property_settings on every selected flat, which is
                // what unlocks admin approval — so refetch both.
                await Promise.all([fetchSettings(), fetchProperties()]);

                closeModal();
              }
            } catch (err) {
              toast.error(
                err.response?.data?.message || "Something went wrong"
              );
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
      <ToastContainer />
    </>
  );
};

export default PropertySetting;
